import { useEffect, useState } from "react";
import { apiClient } from "@/lib/api-client.js";

interface UserItem {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  createdAt: string;
}

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin", management: "Management", planner: "Planer",
  sales: "Vertrieb", technician: "Techniker", viewer: "Betrachter",
  installer: "Installateur (nur Kalkulation)",
};

export function UsersPage() {
  const [items, setItems] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", firstName: "", lastName: "", role: "planner" });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ firstName: "", lastName: "", role: "planner", isActive: true, password: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await apiClient.get<UserItem[]>("/users");
    if (res.success) setItems(res.data);
    setLoading(false);
  };

  useEffect(() => { void load(); }, []);

  const set = (field: keyof typeof form, value: string) => setForm(f => ({ ...f, [field]: value }));

  const startEdit = (u: UserItem) => {
    setEditId(u.id);
    setEditForm({ firstName: u.firstName, lastName: u.lastName, role: u.role, isActive: u.isActive, password: "" });
    setShowForm(false);
    setError(null);
  };
  const setEdit = (field: keyof typeof editForm, value: string | boolean) =>
    setEditForm(f => ({ ...f, [field]: value }));

  const handleUpdate = async () => {
    if (!editId) return;
    setSavingEdit(true); setError(null);
    const body: Record<string, unknown> = {
      firstName: editForm.firstName, lastName: editForm.lastName,
      role: editForm.role, isActive: editForm.isActive,
    };
    if (editForm.password) body.password = editForm.password; // leer = unverändert
    const res = await apiClient.put<UserItem>(`/users/${editId}`, body);
    if (res.success) { setEditId(null); void load(); }
    else { setError((res as { success: false; error: { message: string } }).error.message); }
    setSavingEdit(false);
  };

  const handleCreate = async () => {
    setCreating(true); setError(null);
    const res = await apiClient.post<UserItem>("/users", form);
    if (res.success) {
      setShowForm(false);
      setForm({ email: "", password: "", firstName: "", lastName: "", role: "planner" });
      void load();
    } else { setError((res as { success: false; error: { message: string } }).error.message); }
    setCreating(false);
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Benutzer</h1>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? "Abbrechen" : "+ Neuer Benutzer"}
        </button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Neuer Benutzer</h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div><label>Vorname *</label><input value={form.firstName} onChange={e => set("firstName", e.target.value)} /></div>
            <div><label>Nachname *</label><input value={form.lastName} onChange={e => set("lastName", e.target.value)} /></div>
            <div><label>E-Mail *</label><input type="email" value={form.email} onChange={e => set("email", e.target.value)} /></div>
            <div><label>Passwort *</label><input type="password" value={form.password} onChange={e => set("password", e.target.value)} placeholder="mind. 8 Zeichen" /></div>
            <div><label>Rolle</label><select value={form.role} onChange={e => set("role", e.target.value)}>{Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
          </div>
          {error && <div style={{ color: "var(--color-error)", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-secondary" onClick={() => setShowForm(false)}>Abbrechen</button>
            <button className="btn-primary" onClick={() => void handleCreate()} disabled={creating || !form.email || !form.firstName || !form.lastName || !form.password}>
              {creating ? "Erstelle…" : "Benutzer anlegen"}
            </button>
          </div>
        </div>
      )}

      {editId && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Benutzer bearbeiten</h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div><label>Vorname</label><input value={editForm.firstName} onChange={e => setEdit("firstName", e.target.value)} /></div>
            <div><label>Nachname</label><input value={editForm.lastName} onChange={e => setEdit("lastName", e.target.value)} /></div>
            <div><label>Rolle</label><select value={editForm.role} onChange={e => setEdit("role", e.target.value)}>{Object.entries(ROLE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><label>Status</label><select value={editForm.isActive ? "1" : "0"} onChange={e => setEdit("isActive", e.target.value === "1")}><option value="1">Aktiv</option><option value="0">Inaktiv (gesperrt)</option></select></div>
            <div style={{ gridColumn: "1 / span 2" }}>
              <label>Neues Passwort <span style={{ color: "var(--color-text-muted)", fontWeight: 400 }}>(leer lassen = unverändert)</span></label>
              <input type="password" value={editForm.password} onChange={e => setEdit("password", e.target.value)} placeholder="mind. 8 Zeichen" autoComplete="new-password" />
            </div>
          </div>
          {error && <div style={{ color: "var(--color-error)", marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-secondary" onClick={() => setEditId(null)}>Abbrechen</button>
            <button className="btn-primary" onClick={() => void handleUpdate()} disabled={savingEdit || !editForm.firstName || !editForm.lastName || (editForm.password.length > 0 && editForm.password.length < 8)}>
              {savingEdit ? "Speichere…" : "Speichern"}
            </button>
          </div>
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ textAlign: "center", padding: 48, color: "var(--color-text-muted)" }}>Lade Benutzer…</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: "center", padding: 48, color: "var(--color-text-muted)" }}>Keine Benutzer.</div>
        ) : (
          <>
            <div style={{ marginBottom: 12, fontSize: 13, color: "var(--color-text-muted)" }}>{items.length} Benutzer</div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ borderBottom: "2px solid var(--color-border)" }}>
                {["Name", "E-Mail", "Rolle", "Status", "Angelegt", ""].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 12px", fontSize: 12, color: "var(--color-text-muted)", fontWeight: 600 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {items.map((u, i) => (
                  <tr key={u.id} style={{ borderBottom: "1px solid var(--color-border)", background: i % 2 === 0 ? "transparent" : "#fafafa" }}>
                    <td style={{ padding: "10px 12px", fontWeight: 500 }}>{u.firstName} {u.lastName}</td>
                    <td style={{ padding: "10px 12px", fontSize: 13 }}>{u.email}</td>
                    <td style={{ padding: "10px 12px" }}><span className="badge badge-draft">{ROLE_LABELS[u.role] ?? u.role}</span></td>
                    <td style={{ padding: "10px 12px" }}><span style={{ color: u.isActive ? "var(--color-success)" : "var(--color-error)", fontSize: 13 }}>{u.isActive ? "Aktiv" : "Inaktiv"}</span></td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: "var(--color-text-muted)" }}>{new Date(u.createdAt).toLocaleDateString("de-AT")}</td>
                    <td style={{ padding: "10px 12px", textAlign: "right" }}>
                      <button className="btn-secondary" style={{ fontSize: 12, padding: "4px 10px" }} onClick={() => startEdit(u)}>Bearbeiten</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
